import { useEffect, useState } from "react";

/**
 * Lightweight Google Cast Sender SDK integration. Uses the default media
 * receiver (no Cast App ID required) so this works for any self-hosted setup.
 *
 * Constraints:
 *  - The browser must support Cast (Chrome / Edge / supported derivatives).
 *  - The Cast device on the LAN must be able to fetch the media URL — that
 *    means HTTPS reachable to the device (Let's Encrypt over WAN typically OK)
 *    or HTTP on the same LAN.
 *  - The Cast SDK itself must be loaded over HTTPS (or localhost for dev).
 */
export interface CastApi {
  /** SDK loaded + chrome.cast.framework available */
  ready: boolean;
  /** A Cast device has been discovered on the network */
  available: boolean;
  /** Currently connected to a Cast device */
  casting: boolean;
  play: (m: { url: string; contentType: string; title?: string }) => void;
  stop: () => void;
}

const SDK_URL = "https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1";

declare global {
  interface Window {
    __mvCastLoaderStarted?: boolean;
    __onGCastApiAvailable?: (isAvailable: boolean) => void;
    chrome?: any;
    cast?: any;
  }
}

function loadSdk(): Promise<boolean> {
  if (typeof window === "undefined") return Promise.resolve(false);
  if (window.chrome?.cast?.framework) return Promise.resolve(true);
  if (window.__mvCastLoaderStarted) {
    // Wait for the existing load to finish
    return new Promise((resolve) => {
      const t = setInterval(() => {
        if (window.chrome?.cast?.framework) {
          clearInterval(t);
          resolve(true);
        }
      }, 200);
      setTimeout(() => {
        clearInterval(t);
        resolve(Boolean(window.chrome?.cast?.framework));
      }, 8000);
    });
  }
  window.__mvCastLoaderStarted = true;

  return new Promise((resolve) => {
    window.__onGCastApiAvailable = (isAvailable: boolean) => {
      resolve(isAvailable);
    };
    const s = document.createElement("script");
    s.src = SDK_URL;
    s.async = true;
    s.onerror = () => resolve(false);
    document.head.appendChild(s);
    // Give up after 8s — non-Chrome browsers will silently never resolve.
    setTimeout(() => resolve(Boolean(window.chrome?.cast?.framework)), 8000);
  });
}

export function useCast(): CastApi {
  const [ready, setReady] = useState(false);
  const [available, setAvailable] = useState(false);
  const [casting, setCasting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadSdk().then((ok) => {
      if (cancelled || !ok) return;
      const cast = window.cast;
      const chrome = window.chrome;
      if (!cast?.framework || !chrome?.cast) return;

      try {
        const ctx = cast.framework.CastContext.getInstance();
        ctx.setOptions({
          receiverApplicationId: chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
          autoJoinPolicy: chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
        });
        setReady(true);

        const updateAvailability = (state: string) => {
          // states: NO_DEVICES_AVAILABLE | NOT_CONNECTED | CONNECTING | CONNECTED
          setAvailable(state !== "NO_DEVICES_AVAILABLE");
          setCasting(state === "CONNECTED");
        };
        updateAvailability(ctx.getCastState());
        ctx.addEventListener(
          cast.framework.CastContextEventType.CAST_STATE_CHANGED,
          (ev: { castState: string }) => updateAvailability(ev.castState),
        );
      } catch (err) {
        console.error("cast init failed", err);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  function play(m: { url: string; contentType: string; title?: string }): void {
    const cast = window.cast;
    const chrome = window.chrome;
    if (!cast?.framework || !chrome?.cast) return;
    const ctx = cast.framework.CastContext.getInstance();
    const session = ctx.getCurrentSession();

    const sendLoad = (s: any): void => {
      const mediaInfo = new chrome.cast.media.MediaInfo(m.url, m.contentType);
      mediaInfo.metadata = new chrome.cast.media.GenericMediaMetadata();
      if (m.title) mediaInfo.metadata.title = m.title;
      const request = new chrome.cast.media.LoadRequest(mediaInfo);
      s.loadMedia(request).then(
        () => setCasting(true),
        (err: unknown) => console.error("cast load failed", err),
      );
    };

    if (session) {
      sendLoad(session);
    } else {
      ctx.requestSession().then(
        () => {
          const s2 = ctx.getCurrentSession();
          if (s2) sendLoad(s2);
        },
        (err: unknown) => console.error("cast requestSession failed", err),
      );
    }
  }

  function stop(): void {
    const cast = window.cast;
    if (!cast?.framework) return;
    const ctx = cast.framework.CastContext.getInstance();
    const session = ctx.getCurrentSession();
    session?.endSession(true);
    setCasting(false);
  }

  return { ready, available, casting, play, stop };
}
